import React, { useCallback, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import type { VideoTimeUpdate } from '@rover/components/src/video/VideoPlayer';
import Close from '@rover/icons/svg/close.svg';
import { Box, Button, Flex, Paragraph, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';
import LightboxPager from '@rover/react-lib/src/components/ImageGallery/LightboxPager';
import useResizeObserver from '@rover/react-lib/src/hooks/useResizeObserver';
import { isReactNative } from '@rover/rsdk/src/modules/Env/react';

import { MAX_LIGHTBOX_IMAGE_WIDTH } from './constants';
import type { GalleryItem, GalleryVideoItem, PaginationConfig } from './ImageGallery.common';
import { countGalleryVideoItems } from './ImageGallery.common';

type LightboxProps = {
  images: GalleryItem[] | undefined;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onViewGallery?: () => void;
  pagination?: PaginationConfig;
  renderImageFooter?: (index: number) => React.ReactNode;
  dimmedImageIds?: Set<number>;
  autoPlayVideos?: boolean;
  onVideoPlay?: (item: GalleryVideoItem) => void;
  onVideoTimeUpdate?: (update: VideoTimeUpdate) => void;
  onVideoSeek?: () => void;
  centerVertically?: boolean;
};

const Lightbox = ({
  images,
  lightboxIndex,
  setLightboxIndex,
  onClose,
  onViewGallery,
  pagination,
  renderImageFooter,
  dimmedImageIds,
  autoPlayVideos,
  onVideoPlay,
  onVideoTimeUpdate,
  onVideoSeek,
  centerVertically,
}: LightboxProps): JSX.Element | null => {
  const { t } = useLingui();
  const { colors } = useTheme();

  const videoItemCount = countGalleryVideoItems(images);
  const totalImageCount =
    pagination === undefined ? undefined : pagination.totalImageCount + videoItemCount;

  const belowStageRef = useRef<HTMLElement>(null);
  const [belowStageHeight, setBelowStageHeight] = useState(0);
  const measureBelowStage = useCallback(() => {
    setBelowStageHeight(belowStageRef.current?.offsetHeight ?? 0);
  }, []);
  useResizeObserver(belowStageRef, measureBelowStage);

  const displayTotal = totalImageCount ?? images?.length ?? 0;

  if (!images) return null;

  const currentImage = images[lightboxIndex];

  return (
    <Flex flex="1" alignItems="center" justifyContent="flex-start" flexDirection="column" gap="5x">
      <Flex
        justifyContent={onViewGallery ? 'space-between' : 'flex-end'}
        flexDirection="row"
        width="100%"
        px={['4x', '0x']}
      >
        {onViewGallery && (
          <Button onClick={onViewGallery} contrast>
            {t`View gallery`}
          </Button>
        )}
        <Button
          variant="flat"
          icon={Close}
          onClick={onClose}
          aria-label={t`Dismiss`}
          sx={{
            color: colors.text.primary_inverse,
            '&:hover, &:focus': { color: colors.interactive_link.secondary_inverse_pressed },
          }}
          circular
          contrast
        />
      </Flex>
      <Flex
        flex="1"
        alignItems="center"
        // Native's stage has a fixed height, so centering would shift the counter and photo
        // whenever the footer under them changes height. Web's stage fills the space instead.
        justifyContent={isReactNative() && !centerVertically ? 'flex-start' : 'center'}
        flexDirection="column"
        width="100%"
      >
        <Text size="100" textColor="primary_inverse" mb="4x">
          <Trans>
            {lightboxIndex + 1} of {displayTotal}
          </Trans>
        </Text>
        <LightboxPager
          images={images}
          lightboxIndex={lightboxIndex}
          setLightboxIndex={setLightboxIndex}
          pagination={pagination}
          totalImageCount={totalImageCount}
          belowStageHeight={belowStageHeight}
          dimmedImageIds={dimmedImageIds}
          autoPlayVideos={autoPlayVideos}
          onVideoPlay={onVideoPlay}
          onVideoTimeUpdate={onVideoTimeUpdate}
          onVideoSeek={onVideoSeek}
        />
        <Flex htmlRef={belowStageRef} flexDirection="column" alignItems="center" width="100%">
          {renderImageFooter?.(lightboxIndex)}
          <Box aria-hidden>
            {currentImage?.caption && (
              <Paragraph
                size="200"
                mt="10x"
                px="4x"
                textAlign="center"
                textColor="primary_inverse"
                webProps={{ maxWidth: `${MAX_LIGHTBOX_IMAGE_WIDTH}px` }}
              >
                {currentImage.caption}
              </Paragraph>
            )}
          </Box>
          {currentImage?.formattedAddedDate && (
            <Paragraph
              size="100"
              mt={currentImage.caption ? '2x' : '10x'}
              textColor="primary_inverse"
            >
              <Trans>Uploaded {currentImage.formattedAddedDate}</Trans>
            </Paragraph>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default Lightbox;
